import { useNavigate } from "react-router-dom";
import { TextField, Button, CardMedia, Input } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import { ChangeEvent, useState, useEffect } from "react";
import PhoneInput from "react-phone-input-2";
import { useSelector } from "react-redux";
import { useUploadMediaMutation } from "../../service/common/api";
import { useGetProfileQuery, useUpdateProfileMutation } from "../../service/auth/api";
import CommonBreadcrumb from "../../components/CommonBreadcrumb";


import { toast } from "react-toastify";
import OtpVerifyModal from "../../Modals/OtpVerifyModal";

const EditProfile = () => {

  const navigate = useNavigate();
  const { user } = useSelector((state: any) => state.auth);
  const { data: profileData } = useGetProfileQuery(undefined);
  const [updateProfile, { isLoading: isUpdating }] = useUpdateProfileMutation();

  const [uploadMedia, { isLoading: isUploading }] = useUploadMediaMutation();

  const [alertMessage, setAlertMessage] = useState("");
  const [showAlert, setShowAlert] = useState(false);
  const [alertType, setAlertType] = useState(0);
  const [image, setImage] = useState<string>("");
  const [imageUrl, setImageUrl] = useState<string>("");
  const [fileName, setFileName] = useState<string>("");

  const [verifyModalOpen, setVerifyModalOpen] = useState(false);
  const [verifyTarget, setVerifyTarget] = useState({ type: "email" as "email" | "phone", value: "", countryCode: "" });
  const [isVerified, setIsVerified] = useState({ email: false, phone: false });

  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    countryCode: "+",
    role: 1 // Default role, will be updated from profile
  });

  const [initialFormData, setInitialFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    countryCode: "+",
    role: 1
  });

  useEffect(() => {
    const currentUser = profileData?.data || user;
    if (currentUser) {
      const initialData = {
        fullName: currentUser.fullName || "",
        email: currentUser.email || "",
        phone: currentUser.phone || "",
        countryCode: currentUser.countryCode || "+91",
        role: currentUser.role || 1
      };
      setFormData(initialData);
      setInitialFormData(initialData);

      if (currentUser.image) {
        setImage(currentUser.image);
        setImageUrl(currentUser.image);
      }
    }
  }, [user, profileData]);

  const handleVerifyOpen = (type: "email" | "phone") => {
    if (type === "phone") {
      if (!formData.phone || formData.phone.length < 7 || formData.phone.length > 15) {
        toast.error("Please enter a valid phone number (7-15 digits)");
        return;
      }
    }
    setVerifyTarget({
      type,
      value: type === "email" ? formData.email : formData.phone,
      countryCode: formData.countryCode
    });
    setVerifyModalOpen(true);
  };

  const handleVerifySuccess = () => {
    if (verifyTarget.type === "email") {
      setIsVerified(prev => ({ ...prev, email: true }));
    } else {
      setIsVerified(prev => ({ ...prev, phone: true }));
    }
    // Optionally update initialFormData here if verification counts as a save, 
    // but usually user still needs to click Save. 
    // We will just mark it as verified locally so the button might change state if we wanted.
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;

    // Prevent starting with a space
    if (value.length === 1 && value === ' ') return;

    if (id === 'fullName' && value.length > 40) return;
    if (id === 'email' && value.length > 50) return;
    setFormData({ ...formData, [id]: value });
  };

  const handleSave = async () => {
    // Validation
    if (!formData.fullName.trim()) {
      toast.error("Name is required");
      return;
    }
    if (formData.fullName.length > 40) {
      toast.error("Name must be less than 40 characters");
      return;
    }
    // Email validation if enabled, currently assuming standard email regex if needed, but length check is primary request
    if (formData.email.length > 50) {
      toast.error("Email must be less than 50 characters");
      return;
    }

    try {
      const payload = {
        fullName: formData.fullName,
        image: imageUrl,
        role: formData.role
      };

      // Assuming phone/email updates might require separate verification flow or are not part of this specific update endpoint based on curl
      // But purely based on curl provided:
      // { "image": "...", "fullName": "...", "role": 3 }

      const res = await updateProfile(payload).unwrap();
      toast.success(res?.message || "Profile updated successfully");
      navigate("/manage-profile");

    } catch (err: any) {
      console.error("Update Error:", err);
      toast.error(err?.data?.message || "Failed to update profile");
    }
  };

  return (
    <>
      <CommonBreadcrumb
        title="Edit Profile"
        breadcrumbItems={[
          { label: "Dashboard", onClick: () => navigate("/dashboard") },
          { label: "Manage Profile", onClick: () => navigate("/manage-profile") },
          { label: "Edit Profile" },
        ]}
      />

      <div className="cards">
        <div className="form">
          <div className="gap_p">
            <div className="control_group w_100">
              <label htmlFor="bannerimage">Upload Image</label>
              {image ? (
                <div className="upload_image">
                  <div className="upload_image_holder">
                    <figure>
                      <CardMedia
                        component="img"
                        image={image}
                        alt="photo"
                      />
                    </figure>
                    <CloseIcon
                      onClick={() => {
                        setImage("");
                        setImageUrl("");
                        setFileName("");
                      }}
                    />
                  </div>
                </div>
              ) : (
                <label
                  className="upload_image"
                  htmlFor="icon-button-file"
                >
                  <Input
                    sx={{ display: "none" }}
                    id="icon-button-file"
                    type="file"
                    inputProps={{
                      accept: "image/png,image/jpeg",
                    }}
                    onChange={async (
                      event: ChangeEvent<HTMLInputElement>
                    ) => {
                      const files = (
                        event.target as HTMLInputElement
                      ).files;
                      if (
                        files &&
                        files[0].type.includes("image")
                      ) {
                        const file = files[0];
                        setFileName(file.name);

                        // Create preview locally
                        setImage(URL.createObjectURL(file));

                        // Upload Image
                        const formPayload = new FormData();
                        formPayload.append("file", file);

                        try {
                          const result = await uploadMedia(formPayload).unwrap();
                          // Assuming result.data.url or result.url holds the uploaded image path
                          const uploadedPath = result?.data?.url || result?.url || result?.data;
                          if (uploadedPath) {
                            setImageUrl(uploadedPath);
                            toast.success(result.message || "Image uploaded successfully");
                          } else {
                            // Fallback if structure is different, log it
                            // console.log("Upload Result Structure:", result);
                            toast.warning("Image uploaded but URL not found");
                          }

                        } catch (err: any) {
                          console.error("Upload Error:", err);
                          toast.error(err?.data?.message || "Failed to upload image");
                          // Revert preview if needed
                          setImage("");
                        }

                      } else {
                        setAlertType(0);
                        setShowAlert(true);
                        setAlertMessage(
                          "This field only accepts images."
                        );
                      }
                    }}
                  />
                  <span className="upload_image_holder">
                    <figure className="add_img">
                      <img
                        src="/static/images/user_placeholder.png"
                        alt="icon"
                      />
                      <span className="upload_img_icon">
                        <img
                          src="/static/images/edit2_icon.svg"
                          alt="icon"
                        />
                      </span>
                    </figure>
                  </span>
                </label>
              )}
            </div>
            <div className="control_group w_33">
              <label htmlFor="fullName">Name</label>
              <TextField
                id="fullName"
                placeholder="Enter Name"
                hiddenLabel
                fullWidth
                value={formData.fullName}
                onChange={handleChange}
                inputProps={{ maxLength: 40 }}
              />
            </div>
            <div className="control_group w_33">
              <label htmlFor="email">Email Id</label>
              <TextField
                id="email"
                placeholder="Enter Email Id"
                hiddenLabel
                fullWidth
                value={formData.email}
                onChange={handleChange}
                disabled
                inputProps={{ maxLength: 50 }}
              />
            </div>
            <div className="control_group w_33">
              <label htmlFor="phone">Phone Number</label>
              <PhoneInput
                country={"in"}
                value={`${formData.countryCode} ${formData.phone}`}
                onChange={(value, data: any) => {
                  const dialCode = data.dialCode;
                  // value includes dialCode, so we need to remove it to get the phone number
                  const phoneNumber = value.slice(dialCode.length);
                  setFormData({
                    ...formData,
                    phone: phoneNumber,
                    countryCode: `+${dialCode}`
                  });
                }}
                placeholder="Enter Phone Number"
                enableSearch={false}
                autoFormat={false}
                inputStyle={{ width: "100%" }}
                inputProps={{
                  name: 'phone',
                  required: true,
                  maxLength: 15
                }}
              />
              {((formData.phone !== initialFormData.phone) || (formData.countryCode !== initialFormData.countryCode)) && !isVerified.phone && (
                <div style={{ marginTop: '5px' }}>
                  <Button
                    className="btn btn_primary"
                    variant="contained"
                    size="small"
                    onClick={() => handleVerifyOpen("phone")}
                    sx={{ width: '100%', maxWidth: '100px' }}
                  >
                    Verify
                  </Button>
                </div>
              )}
              {((formData.phone !== initialFormData.phone) || (formData.countryCode !== initialFormData.countryCode)) && isVerified.phone && (
                <div style={{ marginTop: '5px', color: 'green', fontSize: '0.8rem', fontWeight: 'bold' }}>
                  Phone Verified
                </div>
              )}
            </div>
          </div>
          <div className="form_btn">
            <Button className="btn btn_primary" onClick={handleSave} disabled={isUpdating}>
              {isUpdating ? "Saving..." : "Save"}
            </Button>
          </div>
        </div>
      </div>
      <OtpVerifyModal
        open={verifyModalOpen}
        onClose={() => setVerifyModalOpen(false)}
        targetIdentifier={verifyTarget.value}
        targetType={verifyTarget.type}
        countryCode={verifyTarget.countryCode}
        onVerifySuccess={handleVerifySuccess}
      />
    </>
  );
};

export default EditProfile;
